/**
 * 売上の実績。日ごと・月ごとに並べて出す。
 *
 * なぜ作るか:
 *   これまでの「売上分析」はスタッフ別・言語別が中心で、
 *   「今月いくら売れたか」を日ごとに並べた表が無かった。
 *   店主がいちばん見たいのはそこ。
 *
 *   税抜・税額・割引の内訳も出していなかった。
 *   合計だけでは、税務や原価の話に使えない。
 *
 * 計算は utils/salesReport.ts の1箇所に任せる。
 * 画面で足し算を書くと、他の画面と数字が食い違う。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, InputLabel, MenuItem, Select, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  summarizeSales, totalOf, byPaymentMethod,
  type CheckoutLike, type SalesBucket,
} from '../utils/salesReport';

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

const METHOD_LABEL: Record<string, string> = {
  cash: '現金', card: 'クレジットカード', qr: 'QR決済', paypay: 'PayPay',
  receivable: 'ツケ（掛け）', 不明: '不明',
};

/** 何ヶ月ぶんを見るか */
const RANGES = [
  { months: 1, label: '今月' },
  { months: 3, label: '直近3ヶ月' },
  { months: 6, label: '直近6ヶ月' },
  { months: 12, label: '直近12ヶ月' },
];

const SalesReport: React.FC = () => {
  const { shop } = useAuth() as any;
  const [rows, setRows] = useState<CheckoutLike[]>([]);
  const [months, setMonths] = useState(3);
  const [unit, setUnit] = useState<'day' | 'month'>('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shop?.id) return;
    (async () => {
      try {
        setLoading(true); setError(null);
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
        const pad = (n: number) => String(n).padStart(2, '0');
        const fromKey = `${from.getFullYear()}-${pad(from.getMonth() + 1)}-01`;
        const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const toKey = `${next.getFullYear()}-${pad(next.getMonth() + 1)}-01`;

        // businessDate で引く。料金画面・店舗の一覧と同じ数え方。
        const snap = await getDocs(query(
          collection(db, 'checkouts'),
          where('shopId', '==', shop.id),
          where('businessDate', '>=', fromKey),
          where('businessDate', '<', toKey),
        ));
        setRows(snap.docs.map((d) => d.data() as CheckoutLike));
      } catch (e: any) {
        setError(e?.message ?? '売上を読めませんでした');
      } finally {
        setLoading(false);
      }
    })();
  }, [shop?.id, months]);

  const buckets = useMemo(() => summarizeSales(rows, unit), [rows, unit]);
  const total = useMemo(() => totalOf(buckets), [buckets]);
  const methods = useMemo(() => byPaymentMethod(rows), [rows]);

  /** 棒の長さ。いちばん多い日を 100% にする。 */
  const max = useMemo(
    () => buckets.reduce((m, b) => Math.max(m, b.gross), 0),
    [buckets],
  );

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>売上の実績</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        営業日で区切って数えています（暦の日ではありません）。
        取り消した伝票は入っていません。
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>期間</InputLabel>
          <Select label="期間" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            {RANGES.map((r) => <MenuItem key={r.months} value={r.months}>{r.label}</MenuItem>)}
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={unit}
          onChange={(_, v) => v && setUnit(v)}>
          <ToggleButton value="day">日ごと</ToggleButton>
          <ToggleButton value="month">月ごと</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'お会計の合計', value: yen(total.gross), sub: `${total.slips.toLocaleString()} 伝票` },
          { label: '税抜', value: yen(total.net), sub: `消費税 ${yen(total.tax)}` },
          { label: '伝票単価', value: yen(total.perSlip),
            sub: total.guests > 0 ? `客単価 ${yen(total.perGuest)}` : '客数の記録なし' },
        ].map((c) => (
          <Card key={c.label} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">{c.label}</Typography>
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{c.value}</Typography>
              <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {unit === 'day' ? '日ごとの売上' : '月ごとの売上'}
          </Typography>
          {buckets.length === 0 ? (
            <Alert severity="info">この期間の会計はまだありません。</Alert>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{unit === 'day' ? '営業日' : '月'}</TableCell>
                    <TableCell align="right">伝票</TableCell>
                    <TableCell align="right">税抜</TableCell>
                    <TableCell align="right">消費税</TableCell>
                    <TableCell align="right">割引</TableCell>
                    <TableCell align="right">お会計</TableCell>
                    <TableCell align="right">伝票単価</TableCell>
                    <TableCell sx={{ width: 160 }}>大きさ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {buckets.map((b: SalesBucket) => (
                    <TableRow key={b.key} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{b.key}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{b.slips}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{yen(b.net)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{yen(b.tax)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {b.discount > 0 ? `-${yen(b.discount)}` : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                        {yen(b.gross)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{yen(b.perSlip)}</TableCell>
                      <TableCell>
                        {/* 数字を読まずに、多い日と少ない日が分かるようにする */}
                        <Box sx={{
                          height: 10, borderRadius: 5, bgcolor: 'primary.main',
                          width: max > 0 ? `${Math.max(2, (b.gross / max) * 100)}%` : '2%',
                        }} />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>合計</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{total.slips}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{yen(total.net)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{yen(total.tax)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {total.discount > 0 ? `-${yen(total.discount)}` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{yen(total.gross)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{yen(total.perSlip)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>支払い方法の内訳</Typography>
          {methods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">まだありません。</Typography>
          ) : (
            <Stack spacing={1}>
              {methods.map((m) => (
                <Stack key={m.method} direction="row" alignItems="center" spacing={2}>
                  <Chip size="small" label={METHOD_LABEL[m.method] ?? m.method} sx={{ minWidth: 130 }} />
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{
                      height: 10, borderRadius: 5, bgcolor: 'secondary.main',
                      width: total.gross > 0 ? `${Math.max(2, (m.gross / total.gross) * 100)}%` : '2%',
                    }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 120, textAlign: 'right' }}>
                    {yen(m.gross)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
                    {m.slips} 件
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            締めのときは、ここの「現金」と手元の現金を突き合わせてください。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SalesReport;
